import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../../utils/general";

export const userApi = createApi({
  reducerPath: "userApi",
  tagTypes: ["User", "VerifiedAccounts", "LinkedWallets"],
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    login: builder.mutation({
      query: ({ body }) => ({
        url: "/users/login",
        method: "POST",
        body,
      }),
    }),
    getUser: builder.query({
      query: () => ({
        url: "/users/me",
      }),
      providesTags: ["User"],
      extraOptions: { maxRetries: 8 },
    }),
    getUserById: builder.query({
      query: (id) => ({
        url: `/users/${id}`,
      }),
      providesTags: (result, error, arg) => [
        { type: "User", id: result?.user?._id },
        "User",
      ],
    }),
    createUser: builder.mutation({
      query: ({ body }) => ({
        url: "/users/register",
        method: "POST",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    // Complete profile for OAuth users who have an existing account
    completeProfile: builder.mutation({
      query: ({ body }) => ({
        url: "/users/complete-profile",
        method: "POST",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    editUser: builder.mutation({
      query: ({ body }) => ({
        url: "/users/me",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    getAllUser: builder.query({
      query: (params) => ({
        url: "/users",
        params,
      }),
      providesTags: ["User"],
    }),
    verifyEmail: builder.mutation({
      query: ({ body }) => ({
        url: "/auth/verify-email",
        method: "POST",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    sendVerifyEmail: builder.mutation({
      query: () => ({
        url: "/auth/request-verify-email",
        method: "POST",
      }),
      invalidatesTags: ["User"],
    }),
    getNotifications: builder.query({
      query: () => ({
        url: "/users/notifications",
      }),
      providesTags: ["User"],
    }),
    seenNotifications: builder.mutation({
      query: ({ body }) => ({
        url: "/users/notifications/seen",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        {
          type: "User",
          id: "LIST",
        },
      ],
    }),
    // Link wallet to social auth account (connects wallet for blockchain actions)
    linkWallet: builder.mutation({
      query: ({ body }) => ({
        url: "/auth/connect-wallet",
        method: "POST",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    // Get wallet linking status
    getWalletStatus: builder.query({
      query: () => ({
        url: "/auth/wallet-status",
      }),
      providesTags: ["User"],
    }),
    // Get available OAuth providers
    getAuthProviders: builder.query({
      query: () => ({
        url: "/auth/providers",
      }),
    }),
    // Get verified accounts status
    getVerifiedAccounts: builder.query({
      query: () => ({
        url: "/auth/verified-accounts",
      }),
      providesTags: ["VerifiedAccounts"],
    }),
    // Update display settings for verified accounts
    updateVerifiedAccountsDisplay: builder.mutation({
      query: ({ body }) => ({
        url: "/auth/verified-accounts/display",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VerifiedAccounts", "User"],
    }),
    // Unlink OAuth provider
    unlinkOAuth: builder.mutation({
      query: ({ provider }) => ({
        url: `/auth/link/${provider}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VerifiedAccounts", "User"],
    }),
    
    // =========================================================================
    // Linked Wallets Endpoints
    // =========================================================================
    
    // Get all linked wallets for current user
    getLinkedWallets: builder.query({
      query: () => ({
        url: "/auth/linked-wallets",
      }),
      providesTags: ["LinkedWallets"],
    }),
    
    // Get wallet info before linking (check for existing activities)
    getWalletInfo: builder.query({
      query: (address) => ({
        url: `/auth/wallet-info/${address}`,
      }),
    }),
    
    // Connect and link a wallet
    connectWallet: builder.mutation({
      query: ({ body }) => ({
        url: "/auth/connect-wallet",
        method: "POST",
        body,
      }),
      invalidatesTags: ["User", "LinkedWallets"],
    }),
    
    // Disconnect/unlink a wallet
    disconnectWallet: builder.mutation({
      query: ({ address }) => ({
        url: `/auth/wallet${address ? `?address=${address}` : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User", "LinkedWallets"],
    }),
    
    // Update wallet visibility (show/hide profile for wallet activities)
    updateWalletVisibility: builder.mutation({
      query: ({ address, displayProfile }) => ({
        url: `/auth/linked-wallets/${address}/visibility`,
        method: "PUT",
        body: { displayProfile },
      }),
      invalidatesTags: ["LinkedWallets"],
    }),
    
    // Set a wallet as primary
    setWalletAsPrimary: builder.mutation({
      query: ({ address }) => ({
        url: `/auth/linked-wallets/${address}/primary`,
        method: "PUT",
      }),
      invalidatesTags: ["User", "LinkedWallets"],
    }),
    
    // Delete user account permanently
    deleteUser: builder.mutation({
      query: () => ({
        url: "/users/me",
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),
  }),
});

export const {
  useLoginMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useCreateUserMutation,
  useCompleteProfileMutation,
  useEditUserMutation,
  useGetAllUserQuery,
  useVerifyEmailMutation,
  useSendVerifyEmailMutation,
  useGetNotificationsQuery,
  useSeenNotificationsMutation,
  useGetUserByIdQuery,
  // New auth hooks
  useLinkWalletMutation,
  useGetWalletStatusQuery,
  useGetAuthProvidersQuery,
  useGetVerifiedAccountsQuery,
  useUpdateVerifiedAccountsDisplayMutation,
  useUnlinkOAuthMutation,
  // Linked wallets hooks
  useGetLinkedWalletsQuery,
  useLazyGetWalletInfoQuery,
  useConnectWalletMutation,
  useDisconnectWalletMutation,
  useUpdateWalletVisibilityMutation,
  useSetWalletAsPrimaryMutation,
  // Delete account
  useDeleteUserMutation,
} = userApi;
